import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const PAGE_SIZE = 50;

// --------------------------------------------------
// Helpers
// --------------------------------------------------

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const number = Number(
    String(value).replace(/,/g, "").replace(/%/g, "").trim()
  );

  return Number.isFinite(number) ? number : null;
};

const formatPrice = (value) => {
  const number = toNumber(value);

  if (number === null) return "-";

  return number.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatPercent = (value) => {
  const number = toNumber(value);

  if (number === null) return "-";

  return (
    <span
      className={
        number > 0
          ? "text-green-600"
          : number < 0
          ? "text-red-600"
          : "text-gray-600"
      }
    >
      {number > 0 ? "+" : ""}
      {number.toFixed(2)}%
    </span>
  );
};

const formatDate = (value) => {
  if (!value || value === "########") return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getDateKey = (value) => {
  if (!value || value === "########") return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getActionCategory = (value) => {
  const action = String(value || "").trim().toLowerCase();

  if (/^(buy|outperform|overweight)/.test(action)) return "Buy";

  if (/^(sell|reduce|underperform|underweight)/.test(action)) {
    return "Sell";
  }

  if (/^(hold|neutral|market perform)/.test(action)) return "Hold";

  if (/^(accumulate|add)/.test(action)) return "Accumulate";

  return "Other";
};

const getActionColor = (action) => {
  switch (getActionCategory(action)) {
    case "Buy":
      return "bg-green-100 text-green-700";

    case "Sell":
      return "bg-red-100 text-red-700";

    case "Hold":
      return "bg-blue-100 text-blue-700";

    case "Accumulate":
      return "bg-yellow-100 text-yellow-700";

    default:
      return "bg-gray-100 text-gray-600";
  }
};


const getTabColor = (tab, selected) => {
  const colors = {
    All: {
      normal: "bg-gray-100 text-gray-700 border-gray-300",
      active: "bg-gray-700 text-white border-gray-700",
    },
    Buy: {
      normal: "bg-green-50 text-green-700 border-green-200",
      active: "bg-green-600 text-white border-green-600",
    },
    Sell: {
      normal: "bg-red-50 text-red-700 border-red-200",
      active: "bg-red-600 text-white border-red-600",
    },
    Hold: {
      normal: "bg-blue-50 text-blue-700 border-blue-200",
      active: "bg-blue-600 text-white border-blue-600",
    },
    Accumulate: {
      normal: "bg-yellow-50 text-yellow-700 border-yellow-200",
      active: "bg-yellow-500 text-white border-yellow-500",
    },
    Other: {
      normal: "bg-purple-50 text-purple-700 border-purple-200",
      active: "bg-purple-600 text-white border-purple-600",
    },
  };

  const color = colors[tab] || colors.Other;

  return selected ? color.active : color.normal;
};

const ACTION_TABS = [
  "All",
  "Buy",
  "Sell",
  "Hold",
  "Accumulate",
  "Other",
];

// --------------------------------------------------
// Main Component
// --------------------------------------------------

const Reports = () => {
  const [reports, setReports] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [brokerage, setBrokerage] = useState("");
  const [sector, setSector] = useState("");
  const [action, setAction] = useState("All");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [potentialFilter, setPotentialFilter] = useState("");

  // Sorting
  const [sortField, setSortField] = useState("RECOMMEND DATE");
  const [sortDirection, setSortDirection] = useState("desc");

  // Pagination
  const [page, setPage] = useState(1);

  // --------------------------------------------------
  // Fetch API
  // --------------------------------------------------

  useEffect(() => {
    let active = true;

    const fetchReports = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/brokerageReport`
        );

        const data = response.data;

        // Supports both direct arrays and { data: [...] }
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];

        if (active) {
          setReports(rows);
        }
      } catch (err) {
        console.error("Error fetching brokerage reports:", err);

        if (active) {
          setError("Unable to load brokerage reports.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchReports();

    return () => {
      active = false;
    };
  }, []);

  // --------------------------------------------------
  // Brokerage Options
  // --------------------------------------------------

  const brokerages = useMemo(() => {
    return [
      ...new Set(
        reports
          .map((row) => row["BROKERAGE NAME"])
          .filter(Boolean)
      ),
    ].sort();
  }, [reports]);

  // --------------------------------------------------
  // Sector Options
  // --------------------------------------------------

  const sectors = useMemo(() => {
    return [
      ...new Set(
        reports
          .map((row) => row["SECTOR"])
          .filter(Boolean)
      ),
    ].sort();
  }, [reports]);

  // --------------------------------------------------
  // Base Filtering
  // --------------------------------------------------

  const baseFiltered = useMemo(() => {
    const query = search.trim().toLowerCase();

    return reports.filter((row) => {
      const stockName = String(row["STOCK NAME"] || "");
      const symbol = String(row["Symbol"] || "");
      const brokerageName = String(row["BROKERAGE NAME"] || "");
      const sectorName = String(row["SECTOR"] || "");

      // Search
      if (query) {
        const searchable = [
          stockName,
          symbol,
          brokerageName,
          sectorName,
        ]
          .join(" ")
          .toLowerCase();

        if (!searchable.includes(query)) return false;
      }

      // Brokerage
      if (brokerage && brokerageName !== brokerage) {
        return false;
      }

      // Sector
      if (sector && sectorName !== sector) {
        return false;
      }

      // Date
      const dateKey = getDateKey(row["RECOMMEND DATE"]);

      if (fromDate && (!dateKey || dateKey < fromDate)) {
        return false;
      }

      if (toDate && (!dateKey || dateKey > toDate)) {
        return false;
      }

      // Potential Returns
      if (potentialFilter !== "") {
        const potential = toNumber(
          row["POTENTIAL RETURNS (%)"]
        );

        if (
          potential === null ||
          potential < Number(potentialFilter)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [
    reports,
    search,
    brokerage,
    sector,
    fromDate,
    toDate,
    potentialFilter,
  ]);

  // --------------------------------------------------
  // Action Counts
  // --------------------------------------------------

  const actionCounts = useMemo(() => {
    const counts = {
      All: baseFiltered.length,
      Buy: 0,
      Sell: 0,
      Hold: 0,
      Accumulate: 0,
      Other: 0,
    };

    baseFiltered.forEach((row) => {
      const category = getActionCategory(row["ACTION"]);

      counts[category] += 1;
    });

    return counts;
  }, [baseFiltered]);

  // --------------------------------------------------
  // Action Filter + Sorting
  // --------------------------------------------------

  const filteredReports = useMemo(() => {
    let data = baseFiltered;

    if (action !== "All") {
      data = data.filter(
        (row) => getActionCategory(row["ACTION"]) === action
      );
    }

    return [...data].sort((a, b) => {
      let valueA;
      let valueB;

      if (sortField === "RECOMMEND DATE") {
        valueA = getDateKey(a[sortField]);
        valueB = getDateKey(b[sortField]);
      } else {
        valueA = toNumber(a[sortField]);
        valueB = toNumber(b[sortField]);
      }

      // Keep missing values at the bottom
      const missingA =
        valueA === null || valueA === undefined || valueA === "";

      const missingB =
        valueB === null || valueB === undefined || valueB === "";

      if (missingA && missingB) return 0;
      if (missingA) return 1;
      if (missingB) return -1;

      if (valueA < valueB) {
        return sortDirection === "asc" ? -1 : 1;
      }

      if (valueA > valueB) {
        return sortDirection === "asc" ? 1 : -1;
      }

      return 0;
    });
  }, [baseFiltered, action, sortField, sortDirection]);

  // --------------------------------------------------
  // Pagination
  // --------------------------------------------------

  const totalPages = Math.max(
    1,
    Math.ceil(filteredReports.length / PAGE_SIZE)
  );

  const currentPage = Math.min(page, totalPages);

  const paginatedReports = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;

    return filteredReports.slice(start, start + PAGE_SIZE);
  }, [filteredReports, currentPage]);

  // --------------------------------------------------
  // Sorting Handler
  // --------------------------------------------------

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) =>
        prev === "asc" ? "desc" : "asc"
      );
    } else {
      setSortField(field);
      setSortDirection("desc");
    }

    setPage(1);
  };

  // --------------------------------------------------
  // Reset Filters
  // --------------------------------------------------

  const resetFilters = () => {
    setSearch("");
    setBrokerage("");
    setSector("");
    setAction("All");

    setFromDate("");
    setToDate("");

    setPotentialFilter("");

    setSortField("RECOMMEND DATE");
    setSortDirection("desc");

    setPage(1);
  };

  // --------------------------------------------------
  // Sortable Header
  // --------------------------------------------------

  const SortHeader = ({ label, field }) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className="flex items-center gap-1 hover:text-blue-600"
    >
      {label}

      <span className="text-xs">
        {sortField === field
          ? sortDirection === "asc"
            ? "↑"
            : "↓"
          : "↕"}
      </span>
    </button>
  );

  // --------------------------------------------------
  // Render
  // --------------------------------------------------

  return (
    <div className="w-full px-2 py-4 text-sm text-gray-800">

      {/* Header */}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold">
            Brokerage Reports
          </h2>

          <p className="text-xs text-gray-500 mt-1">
            {filteredReports.length.toLocaleString("en-IN")} reports
          </p>
        </div>

        <button
          onClick={resetFilters}
          className="border rounded px-3 py-2 text-xs hover:bg-gray-100"
        >
          Reset Filters
        </button>
      </div>

      {/* Filters */}

      <div className="flex flex-wrap items-center gap-2 mb-4">

        {/* Search */}

        <input
          type="text"
          placeholder="Search stock, symbol, brokerage..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="border rounded px-3 py-2 text-xs w-64 max-w-full"
        />

        {/* Brokerage */}

        <select
          value={brokerage}
          onChange={(e) => {
            setBrokerage(e.target.value);
            setPage(1);
          }}
          className="border rounded px-2 py-2 text-xs"
        >
          <option value="">All Brokerages</option>

          {brokerages.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>

        {/* Sector */}

        <select
          value={sector}
          onChange={(e) => {
            setSector(e.target.value);
            setPage(1);
          }}
          className="border rounded px-2 py-2 text-xs"
        >
          <option value="">All Sectors</option>

          {sectors.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>

        {/* Potential Returns */}

        <select
          value={potentialFilter}
          onChange={(e) => {
            setPotentialFilter(e.target.value);
            setPage(1);
          }}
          className="border rounded px-2 py-2 text-xs"
        >
          <option value="">Any Potential</option>
          <option value="0">Potential ≥ 0%</option>
          <option value="10">Potential ≥ 10%</option>
          <option value="20">Potential ≥ 20%</option>
          <option value="30">Potential ≥ 30%</option>
          <option value="50">Potential ≥ 50%</option>
        </select>

        {/* From Date */}

        <label className="flex items-center gap-1 text-xs">
          From
          <input
            type="date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setPage(1);
            }}
            className="border rounded px-2 py-2 text-xs"
          />
        </label>

        {/* To Date */}

        <label className="flex items-center gap-1 text-xs">
          To
          <input
            type="date"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setPage(1);
            }}
            className="border rounded px-2 py-2 text-xs"
          />
        </label>
      </div>

      {/* Action Tabs */}

      <div className="flex gap-2 overflow-x-auto pb-3 mb-3 border-b">

        {ACTION_TABS.map((tab) => {
          const selected = action === tab;

          return (
            <button
              key={tab}
              onClick={() => {
                setAction(tab);
                setPage(1);
              }}
              className={`
                flex items-center gap-2
                flex-shrink-0
                px-3 py-1.5
                rounded-md
                text-xs font-semibold
                border
                transition-all duration-150
                hover:shadow-sm
                ${getTabColor(tab, selected)}
              `}
            >
              {tab}

              <span
                className={`
                  px-1.5 py-0.5
                  rounded-full
                  text-[10px] font-bold
                  ${
                    selected
                      ? "bg-white bg-opacity-20 text-white"
                      : "bg-white bg-opacity-70"
                  }
                `}
              >
                {actionCounts[tab] || 0}
              </span>
            </button>
          );
        })}

      </div>

      {/* Error */}

      {error && (
        <div className="bg-red-50 text-red-600 border border-red-200 rounded p-3 mb-3">
          {error}
        </div>
      )}

      {/* Loading */}

      {loading ? (
        <div className="text-center py-12 text-gray-500">
          Loading brokerage reports...
        </div>
      ) : (
        <>
          {/* Table */}

          <div className="overflow-x-auto border rounded">

            <table className="w-full border-collapse text-xs whitespace-nowrap">

              <thead className="bg-gray-100 text-gray-700">

                <tr className="text-left">

                  <th className="p-2 border-b sticky left-0 bg-gray-100 z-10">
                    Stock
                  </th>

                  <th className="p-2 border-b">
                    <SortHeader
                      label="Date"
                      field="RECOMMEND DATE"
                    />
                  </th>

                  <th className="p-2 border-b">
                    Brokerage
                  </th>

                  <th className="p-2 border-b">
                    Action
                  </th>

                  <th className="p-2 border-b text-right">
                    <SortHeader
                      label="Target"
                      field="TARGET PRICE"
                    />
                  </th>

                  <th className="p-2 border-b text-right">
                    <SortHeader
                      label="Recommended"
                      field="RECOMMENDED PRICE"
                    />
                  </th>

                  <th className="p-2 border-b text-right">
                    <SortHeader
                      label="Current"
                      field="CURRENT PRICE"
                    />
                  </th>

                  <th className="p-2 border-b text-right">
                    <SortHeader
                      label="Return %"
                      field="CURRENT RETURNS (%)"
                    />
                  </th>

                  <th className="p-2 border-b text-right">
                    <SortHeader
                      label="Potential %"
                      field="POTENTIAL RETURNS (%)"
                    />
                  </th>

                  <th className="p-2 border-b">
                    Report
                  </th>

                </tr>

              </thead>

              <tbody>

                {paginatedReports.map((row, index) => {
                  const symbol = String(row["Symbol"] || "").trim();

                  const pdfUrl = String(row["PDF URL"] || "").trim();

                  return (
                    <tr
                      key={`${row["ID"] || symbol}-${index}`}
                      className="border-b hover:bg-gray-50"
                    >

                      {/* Stock */}

                      <td className="p-2 sticky left-0 bg-white z-10">

                        {symbol ? (
                          <a
                            href={`/symbol/${encodeURIComponent(symbol)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 font-semibold hover:underline"
                          >
                            {row["STOCK NAME"] || symbol}
                          </a>
                        ) : (
                          <span className="font-semibold">
                            {row["STOCK NAME"] || "-"}
                          </span>
                        )}

                        {symbol && (
                          <div className="text-gray-400 text-[10px]">
                            {symbol}
                          </div>
                        )}

                      </td>

                      {/* Date */}

                      <td className="p-2">
                        {formatDate(row["RECOMMEND DATE"])}
                      </td>

                      {/* Brokerage */}

                      <td className="p-2">
                        {row["BROKERAGE NAME"] || "-"}
                      </td>

                      {/* Action */}

                      <td className="p-2">

                        <span
                          className={`px-2 py-1 rounded text-[11px] font-semibold ${getActionColor(
                            row["ACTION"]
                          )}`}
                        >
                          {row["ACTION"] || "-"}
                        </span>

                      </td>

                      {/* Target */}

                      <td className="p-2 text-right">
                        {formatPrice(row["TARGET PRICE"])}
                      </td>

                      {/* Recommended */}

                      <td className="p-2 text-right">
                        {formatPrice(row["RECOMMENDED PRICE"])}
                      </td>

                      {/* Current */}

                      <td className="p-2 text-right">
                        {formatPrice(row["CURRENT PRICE"])}
                      </td>

                      {/* Current Returns */}

                      <td className="p-2 text-right">
                        {formatPercent(
                          row["CURRENT RETURNS (%)"]
                        )}
                      </td>

                      {/* Potential Returns */}

                      <td className="p-2 text-right">
                        {formatPercent(
                          row["POTENTIAL RETURNS (%)"]
                        )}
                      </td>

                      {/* PDF */}

                      <td className="p-2">

                        {/^https?:\/\//i.test(pdfUrl) ? (
                          <a
                            href={pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            PDF ↗
                          </a>
                        ) : (
                          "-"
                        )}

                      </td>

                    </tr>
                  );
                })}

              </tbody>

            </table>

            {!paginatedReports.length && (
              <div className="text-center py-10 text-gray-500">
                No brokerage reports found.
              </div>
            )}

          </div>

          {/* Pagination */}

          {filteredReports.length > PAGE_SIZE && (
            <div className="flex items-center justify-between gap-3 mt-4">

              <div className="text-xs text-gray-500">
                Showing{" "}
                {(currentPage - 1) * PAGE_SIZE + 1}
                {" - "}
                {Math.min(
                  currentPage * PAGE_SIZE,
                  filteredReports.length
                )}
                {" of "}
                {filteredReports.length}
              </div>

              <div className="flex items-center gap-2">

                <button
                  disabled={currentPage === 1}
                  onClick={() => setPage(1)}
                  className="border rounded px-3 py-1.5 disabled:opacity-40"
                >
                  First
                </button>

                <button
                  disabled={currentPage === 1}
                  onClick={() =>
                    setPage((prev) => Math.max(1, prev - 1))
                  }
                  className="border rounded px-3 py-1.5 disabled:opacity-40"
                >
                  Previous
                </button>

                <span className="text-xs">
                  {currentPage} / {totalPages}
                </span>

                <button
                  disabled={currentPage >= totalPages}
                  onClick={() =>
                    setPage((prev) =>
                      Math.min(totalPages, prev + 1)
                    )
                  }
                  className="border rounded px-3 py-1.5 disabled:opacity-40"
                >
                  Next
                </button>

                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(totalPages)}
                  className="border rounded px-3 py-1.5 disabled:opacity-40"
                >
                  Last
                </button>

              </div>

            </div>
          )}

        </>
      )}

    </div>
  );
};

export default Reports;